import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Tipos do Mercado Pago
export interface MercadoPagoCheckoutResponse {
  publicKey: string;
  planAmount: number;
  planName: string;
  planType: string;
  customerEmail: string;
  customerName: string;
  description: string;
  maxInstallments: number;
  errorMessage?: string;
}

export interface PrepararCheckoutRequest {
  customerEmail: string;
  customerName: string;
  customerTaxId: string;
  planId?: string; // ID do plano selecionado
}

export interface ProcessarPagamentoCartaoRequest {
  token: string;
  transactionAmount: number;
  description: string;
  installments: number;
  email: string;
  firstName: string;
  lastName: string;
  identificationType: string;
  identificationNumber: string;
  externalReference?: string;
  planType?: string;
}

export interface ProcessarPagamentoCartaoResponse {
  success: boolean;
  paymentId?: string;
  status?: string;
  statusDetail?: string;
  transactionAmount?: number;
  installments?: number;
  errorMessage?: string;
  errors?: Array<{ code: string; message: string }>;
}

/**
 * Prepara checkout inline retornando public key e configurações
 */
export const prepararCheckout = async (
  data: PrepararCheckoutRequest
): Promise<MercadoPagoCheckoutResponse> => {
  const token = localStorage.getItem('@ThinkWay:token');
  
  const response = await axios.post(
    `${API_URL}/api/mercadopago/preparar-checkout`,
    data,
    {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

/**
 * Processa pagamento com cartão tokenizado
 */
export const processarPagamentoCartao = async (
  data: ProcessarPagamentoCartaoRequest
): Promise<ProcessarPagamentoCartaoResponse> => {
  const token = localStorage.getItem('@ThinkWay:token');
  
  try {
    const response = await axios.post(
      `${API_URL}/api/mercadopago/processar-pagamento-cartao`,
      data,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    // Se o backend retornou 400 com dados estruturados, extrair a resposta
    if (error.response?.status === 400 && error.response?.data) {
      console.error('Erro 400 do backend:', error.response.data);
      return error.response.data; // Retornar a resposta do backend com success: false
    }
    // Para outros erros, relançar
    throw error;
  }
};

/**
 * Obtém a chave pública do Mercado Pago
 */
export const getPublicKey = async (): Promise<string> => {
  const token = localStorage.getItem('@ThinkWay:token');
  
  const response = await axios.get(`${API_URL}/api/mercadopago/public-key`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  return response.data.publicKey;
};

/**
 * Valida as credenciais do Mercado Pago
 */
export const validarCredenciais = async (): Promise<{ valid: boolean; message: string }> => {
  const token = localStorage.getItem('@ThinkWay:token');
  
  const response = await axios.get(`${API_URL}/api/mercadopago/validar-credenciais`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
};

/**
 * Cria um pagamento com cartão de crédito via Mercado Pago
 * Retorna a URL do checkout para exibir no iframe
 */
export interface CreateMercadoPagoPaymentRequest {
  planType: string;
  customerEmail: string;
  customerName: string;
  customerTaxId: string;
  customerCellphone?: string;
}

export interface CreateMercadoPagoPaymentResponse {
  error?: string;
  checkoutUrl?: string;
  publicKey?: string;
  planAmount?: number;
  planName?: string;
}

/**
 * Cria um pagamento via Mercado Pago
 * Prepara o checkout inline retornando as configurações necessárias
 */
export const createMercadoPagoPayment = async (
  data: CreateMercadoPagoPaymentRequest
): Promise<CreateMercadoPagoPaymentResponse> => {
  try {
    const token = localStorage.getItem('@ThinkWay:token');
    
    const response = await axios.post(
      `${API_URL}/api/mercadopago/preparar-checkout`,
      {
        customerEmail: data.customerEmail,
        customerName: data.customerName,
        customerTaxId: data.customerTaxId,
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const checkoutData = response.data;

    if (checkoutData.errorMessage) {
      return {
        error: checkoutData.errorMessage,
      };
    }

    return {
      publicKey: checkoutData.publicKey,
      planAmount: checkoutData.planAmount,
      planName: checkoutData.planName,
    };
  } catch (error: any) {
    console.error('Erro ao criar pagamento Mercado Pago:', error);
    return {
      error: error.response?.data?.error || error.message || 'Erro ao criar pagamento',
    };
  }
};

